import {
  type AccountData,
  type AccountUpdateInput,
  type AccountUpdateSuccess,
  Logger,
  toResponseError,
} from '@terros/common'
import { useWidgetCache } from '../widget'
import { useTantalimMutation } from '../mutation'
import { useLocationCache } from '../location'
import { mutationUpdate, type UpdateHookResponse } from '../hooks'
import { useAccountCache } from './cache'

const logger = new Logger('useUpdateAccount')
logger.level = 'verbose'

export function useUpdateAccount(): UpdateHookResponse<AccountUpdateInput['account'], AccountData> {
  const { updateLocationAccount, clearItem } = useLocationCache()
  const { updateItem } = useAccountCache()

  const mutation = useTantalimMutation<AccountUpdateInput, AccountUpdateSuccess>({
    url: '/account/update',
  })
  const widgetCache = useWidgetCache()

  async function update(account: AccountUpdateInput['account']): Promise<AccountData> {
    try {
      logger.verbose('starting update')
      const data = await mutation.mutateAsync({ account })
      logger.verbose('done with update')
      const newAccount = data.account
      try {
        updateItem(newAccount)
        const { locationId } = newAccount
        logger.verbose('updated account locationId', locationId)
        if (locationId) {
          updateLocationAccount(locationId, newAccount)
          clearItem(locationId)
        }
        widgetCache.clearWidgets()
        widgetCache.clearList()
      } catch (error) {
        logger.verbose('error updating cache data after account update', error)
      }
      return newAccount
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationUpdate(mutation, update)
}
